/*
 * File: PrivacyPolicy.jsx
 * Project: lets-talk-web
 *
 * Created by Brendan Michaelsen on February 8, 2022 at 3:21 PM
 * Copyright © 2022 Let's Talk. All rights reserved.
 *
 * Last Modified: May 1, 2024 at 11:23 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// Utilities
import { createStateLocale } from '../../../../utilities/locale';

// Components
import {
	Meta, ContentNavigation, LegalNavigation, Typography, LocaleLink
} from '../../../../components';

// Styles
import * as S from './PrivacyPolicy.styles';
import { HighlightSection } from '../../../../components/LegalNavigation/LegalNavigation.styles';


/**
 * Component
 */

const PrivacyPolicy = ({ meta, locale }) => {

	// Get current locale from hook
	const clientLocale = useSelector((state) => state.locale.value);
	const stateLocale = createStateLocale(clientLocale, locale);

	// Handle component initialization
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} locale={stateLocale} />

			{/* Component Content */}
			<ContentNavigation>
				<LegalNavigation>
					<S.Wrapper>

						{/* Title */}
						<Typography tag="h1" weight="bold">
							Privacy Policy
						</Typography>

						{/* TL;DR */}
						<HighlightSection>
							<Typography tag="p" weight="light">
								By using the Let&apos;s Talk platform, you understand that we gather and securely store certain information about
								you that applies to your use of Let&apos;s Talk. This includes profile information you provide to us, content
								you create, and automated data like IP addresses and device identifiers. We use this information to provide
								a customized experience, improve our technology, and allow trusted third-parties to advertise to you. Please
								read the “Your Choices” section below to learn more about ways you can monitor what data we collect about you.
							</Typography>
						</HighlightSection>

						{/* Last Updated */}
						<Typography tag="p" weight="light" fontStyle="italic">
							Last updated July 2026
						</Typography>

						{/* Content */}
						<Typography tag="p" weight="light">
							This Privacy Policy applies to Let&apos;s Talk services (the “Platform”), which include Let&apos;s Talk apps, websites, software and related services accessed via any platform or device that link to this Privacy Policy. The Platform is operated by
							{' '}
							<Typography tag="span" weight="bold" fontStyle="italic">
								Teen Health Research, Inc. d/b/a Let&apos;s Talk Families.
							</Typography>
							{' '}
							(“Teen Health Research”, “Let&apos;s Talk,” “we,” “our,” or “us”). We are committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, share, and otherwise process the personal information of users and other individuals age 13 and over in connection with our Platform. Please see our
							{' '}
							<a href="#children-privacy">Children&apos;s Privacy</a>
							{' '}
							section below for information about how we collect, use, share, and otherwise process the personal information of children under the age of 13.
						</Typography>
						<Typography tag="p" weight="light">
							Capitalized terms that are not defined in this Privacy Policy have the meaning given to them in the
							{' '}
							<LocaleLink to="/legal/terms" target="_blank">Terms of Service</LocaleLink>
							.
						</Typography>

						{/* What Information We Collect */}
						<br />
						<Typography tag="h3" weight="bold">
							What Information We Collect
						</Typography>
						<Typography tag="p" weight="light">
							We may collect information from and about you, including information that you provide, information from other sources, and automatically collected information.
						</Typography>

						{/* Information You Provide */}
						<Typography tag="h4" weight="bold">
							Information You Provide
						</Typography>
						<Typography tag="p" weight="light">
							When you create an account, upload content, contact us directly, or otherwise use the Platform, you may provide some or all of the following information:
						</Typography>
						<Typography weight="light">
							<ul>
								<li>Account and profile information, such as name, age, username, password, language, email, phone number, social media account information, and profile image.</li>
								<li>User-generated content, including comments, photographs, livestreams, audio recordings, videos, text, hashtags, and virtual item videos that you choose to create with or upload to the Platform (“User Content”) and the associated metadata, such as when, where, and by whom the content was created. Even if you are not a user, information about you may appear in User Content created or published by users on the Platform. When you create User Content, we may upload or import it to the Platform before you save or post the User Content (also known as pre-uploading), for example, in order to recommend audio options, generate captions, and provide other personalized recommendations. If you apply an effect to your User Content, we may collect a version of your User Content that does not include the effect.</li>
								<li>Messages, which include information you provide when you compose, send, or receive messages through the Platform’s messaging functionalities. They include messages you send through our chat functionality when communicating with businesses who sell goods to you, and your use of virtual assistants when purchasing items through the Platform. That information includes the content of the message and information about the message, such as when it was sent, received, or read, and message participants. Please be aware that messages you choose to send to other users of the Platform will be accessible by those users and that we are not responsible for the manner in which those users use or share the messages.</li>
								<li>Information, including text, images, and videos, found in your device’s clipboard, with your permission. For example, if you choose to initiate information sharing with a third-party platform, or choose to paste content from the clipboard onto the Platform, we access this information stored in your clipboard in order to fulfill your request.</li>
								<li>Purchase information, including payment card numbers or other third-party payment information (such as Stripe) where required for the purpose of payment, and billing and shipping address. We also collect information that is required for extended warranty purposes and your transaction and purchase history on or through the Platform.</li>
								<li>Your phone and social network contacts, with your permission. If you choose to find other users through your phone contacts, we will access and collect information such as names, phone numbers, and email addresses, and match that information against existing users of the Platform. If you choose to find other users through your social network contacts, we will collect your public profile information as well as names and profiles of your social network contacts.</li>
								<li>Your choices and communication preferences.</li>
								<li>Information to verify an account such as proof of identity or age.</li>
								<li>Information in correspondence you send to us, including when you contact us for support.</li>
								<li>Information you share through surveys or your participation in challenges, research, promotions, marketing campaigns, events, or contests such as your gender, age, likeness, and preferences.</li>
							</ul>
						</Typography>

						{/* Information From Other Sources */}
						<Typography tag="h4" weight="bold">
							Information From Other Sources
						</Typography>
						<Typography tag="p" weight="light">
							We may receive the information described in this Privacy Policy from other sources, such as:
						</Typography>
						<Typography weight="light">
							<ul>
								<li>If you choose to sign-up or log-in to the Platform using a third-party service such as Google, Microsoft, and Meta, or link your Let&apos;s Talk account to a third-party service, we may collect information from the service–for example, your public profile information (such as nickname), and email.</li>
								<li>We may receive information about you from others, including where you are included or mentioned in User Content, direct messages, in a complaint, appeal, request or feedback submitted to us, or if your contact information is provided to us. We may collect information about you from other publicly available sources.</li>
							</ul>
						</Typography>

						{/* Automatically Collected Information */}
						<Typography tag="h4" weight="bold">
							Automatically Collected Information
						</Typography>
						<Typography tag="p" weight="light">
							We automatically collect certain information from you when you use the Platform, including internet or other network activity information such as your IP address, geolocation-related data, unique device identifiers, browsing and search history (including content you have viewed in the Platform).
						</Typography>
						<Typography weight="light">
							<ul>
								<li>
									<Typography weight="bold">Usage Information.</Typography>
									{' '}
									We collect information regarding your use of the Platform and any other User Content that you generate through or upload to our Platform.
								</li>
								<li>
									<Typography weight="bold">Device Information.</Typography>
									{' '}
									We collect certain information about the device you use to access the Platform, such as your IP address, user agent, mobile carrier, time zone settings, model of your device, the device system, network type, device IDs, and operating system.
								</li>
								<li>
									<Typography weight="bold">Location Data.</Typography>
									{' '}
									We collect information about your approximate location, including location information based on your SIM card and/or IP address. In addition, we collect location information (such as tourist attractions, businesses, or other points of interest) if you choose to add the location information to your User Content. With your permission, we may also collect precise location data (such as GPS).
								</li>
								<li>
									<Typography weight="bold">Metadata.</Typography>
									{' '}
									When you upload or create User Content, you automatically upload certain metadata that is connected to the User Content. Metadata describes other data and provides information about your User Content that will not always be evident to the viewer. For example, in connection with your User Content the metadata can describe how, when, where, and by whom the piece of User Content was created, collected, or modified and how that content is formatted. It also includes information, such as your account name, that enables other users to trace back the User Content to your user account. Additionally, metadata includes data that you choose to provide with your User Content, e.g., any hashtags used to mark keywords to the video and captions.
								</li>
								<li>
									<Typography weight="bold">Cookies.</Typography>
									{' '}
									We and our service providers and business partners use cookies and other similar technologies (e.g., web beacons, flash cookies, etc.) (“Cookies”) to automatically collect information, including how you interact with content, to enhance your experience using the Platform, prevent abuse, ensure fair usage, and improve the Platform. Cookies enable the Platform to provide certain features and functionality. Web beacons are very small images or small pieces of data embedded in images, also known as “pixel tags” or “clear GIFs,” that can recognize Cookies, the time and date a page is viewed, a description of the page where the pixel tag is placed, and similar information from your computer or device.
								</li>
							</ul>
						</Typography>
						<Typography tag="p" weight="light">
							We are not responsible for the privacy practices of our service providers and business partners, and the information practices of these service providers and business partners are not covered by this Privacy Policy.
						</Typography>
						<Typography tag="p" weight="light">
							We may aggregate or de-identify the information described above. Aggregated or de-identified data is not subject to this Privacy Policy.
						</Typography>

						{/* How We Use Your Information */}
						<br />
						<Typography tag="h3" weight="bold">
							How We Use Your Information
						</Typography>
						<Typography tag="p" weight="light">
							As explained below, we use your information to improve, support and administer the Platform, to allow you to use its functionalities, and to fulfill and enforce our Terms of Service. We may also use your information to, among other things, show you suggestions, promote the Platform, and customize your ad experience. Unless otherwise noted, the uses described in the section pertain to both adult and child users of the Platform.
						</Typography>
						<Typography tag="p" weight="light">
							We generally use the Information We Collect:
						</Typography>
						<Typography weight="light">
							<ul>
								<li>To fulfill requests for products, services, Platform functionality, support and information for internal operations, including troubleshooting, data analysis, testing, research, statistical, and survey purposes and to solicit your feedback.</li>
								<li>To customize the content you see when you use the Platform. For example, we may provide you with services based on the country settings you have chosen or show you content that is similar to content that you have liked or interacted with.</li>
								<li>To send promotional materials from us or on behalf of our affiliates and trusted third parties.</li>
								<li>To improve and develop our Platform and conduct product development.</li>
								<li>To measure and understand the effectiveness of the advertisements we serve to you and others and to deliver advertising, including targeted advertising, to you on the Platform.</li>
								<li>To make suggestions and provide a customized ad experience.</li>
								<li>To support the social functions of the Platform, including to permit you and others to connect with each other (for example, through our Find Friends function), to suggest accounts to you and others, and for you and others to share, download, and otherwise interact with User Content posted through the Platform.</li>
								<li>To use User Content as part of our advertising and marketing campaigns to promote the Platform.</li>
								<li>To understand how you use the Platform, including across your devices.</li>
								<li>To infer additional information about you, such as your age, gender, and interests.</li>
								<li>To help us detect abuse, fraud, and illegal activity on the Platform.</li>
								<li>
									To promote the safety and security of the Platform, including by scanning, analyzing, and reviewing User Content, messages and associated metadata for violations of our
									{' '}
									<LocaleLink to="/legal/terms" target="_blank">Terms of Service</LocaleLink>
									, or other conditions and policies.
								</li>
								<li>To verify your identity in order to use certain features, such as livestream or verified accounts to ensure that you are old enough to use the Platform (as required by law), or in other instances where verification may be required.</li>
								<li>To communicate with you, including to notify you about changes in our services.</li>
								<li>To announce you as a winner of our contests or promotions if permitted by the promotion rule, and to send you any applicable prizes.</li>
								<li>To enforce our Terms of Service, and other conditions and policies.</li>
								<li>Consistent with your permissions, to provide you with location-based services, such as advertising and other personalized content.</li>
								<li>To train and improve our technology, such as our machine learning models and algorithms.</li>
								<li>To combine all the Information We Collect or receive about you for any of the foregoing purposes.</li>
								<li>To facilitate sales, promotion, and purchases of goods and services and to provide user support.</li>
								<li>For any other purposes disclosed to you at the time we collect your information or pursuant to your consent.</li>
							</ul>
						</Typography>

						{/* How We Share Your Information */}
						<br />
						<Typography tag="h3" weight="bold">
							How We Share Your Information
						</Typography>
						<Typography tag="p" weight="light">
							We are committed to maintaining your trust, and while Let&apos;s Talk does not sell your personal information or share your personal information with third parties for purposes of cross-context behavioral advertising where restricted by applicable law, we want you to understand when and with whom we may share the Information We Collect for business purposes.
						</Typography>

						{/* Service Providers and Business Partners */}
						<Typography tag="h4" weight="bold">
							Service Providers and Business Partners
						</Typography>
						<Typography tag="p" weight="light">
							We share the categories of personal information listed above with service providers and business partners to help us perform business operations and for business purposes, including research, payment processing and transaction fulfillment, database maintenance, administering contests and special offers, technology services, deliveries, sending communications, advertising and marketing services, analytics, measurement, data storage and hosting, disaster recovery, search engine optimization, and data processing. These service providers and business partners may include:
						</Typography>
						<Typography weight="light">
							<ul>
								<li>
									Payment processors and transaction fulfillment providers, who may receive the Information You Provide, Information From Other Sources, and Automatically Collected Information but who do not receive your message data, including, in particular, the following third-party payment providers/processors: Stripe (
									<LocaleLink to="https://stripe.com/en-ie/privacy" target="_blank">https://stripe.com/en-ie/privacy</LocaleLink>
									). Payment card information is collected and processed directly by Stripe and is not stored on our systems.
								</li>
								<li>Customer and technical support providers, who may receive the Information You Provide, Information From Other Sources, and Automatically Collected Information.</li>
								<li>Researchers who may receive the Information You Provide, Information From Other Sources, and Automatically Collected Information but would not receive your payment information or message data.</li>
								<li>Advertising, marketing, and analytics vendors, who may receive the Information You Provide, Information From Other Sources, and Automatically Collected Information but would not receive your payment information or message data.</li>
							</ul>
						</Typography>

						{/* Within Our Corporate Group */}
						<Typography tag="h4" weight="bold">
							Within Our Corporate Group
						</Typography>
						<Typography tag="p" weight="light">
							We may share all of the Information We Collect with a parent, subsidiary, or other affiliate of our corporate group.
						</Typography>

						{/* In Connection with a Sale, Merger, or Other Business Transfer */}
						<Typography tag="h4" weight="bold">
							In Connection with a Sale, Merger, or Other Business Transfer
						</Typography>
						<Typography tag="p" weight="light">
							We may share all of the Information We Collect in connection with a substantial corporate transaction, such as the sale of a website, a merger, consolidation, asset sales, or in the unlikely event of bankruptcy.
						</Typography>

						{/* For Legal Reasons */}
						<Typography tag="h4" weight="bold">
							For Legal Reasons
						</Typography>
						<Typography tag="p" weight="light">
							We may disclose any of the Information We Collect to respond to subpoenas, court orders, legal process, law enforcement requests, legal claims, or government inquiries, and to protect and defend the rights, interests, safety, and security of the Platform, our affiliates, users, or the public. We may also share any of the Information We Collect to enforce any terms applicable to the Platform, to exercise or defend any legal claims, and comply with any applicable law.
						</Typography>

						{/* With Your Consent */}
						<Typography tag="h4" weight="bold">
							With Your Consent
						</Typography>
						<Typography tag="p" weight="light">
							We may share your information for other purposes pursuant to your consent or at your direction.
						</Typography>
						<Typography tag="p" weight="light">
							We partner with third-party services (such as Google, Microsoft and Meta) to offer you a seamless sign-up, log-in, and content-sharing experience. We may share information about you with these third-party services if you choose to use these features. For example, the services may receive information about your activity on the Platform and may notify your connections on the third-party services about your use of the Platform, in accordance with their privacy policies. If you choose to allow a third-party service to access your account, we will share certain information about you with the third party. Depending on the permissions you grant, the third party may be able to obtain your account information and other information you choose to provide.
						</Typography>
						<Typography tag="p" weight="light">
							If you choose to engage in public activities on the Platform, you should be aware that any information you share may be read, collected, or used by other users. You should use caution in disclosing personal information while using the Platform. We are not responsible for the information you choose to submit.
						</Typography>
						<Typography tag="p" weight="light">
							When you make a purchase from a third party on the Platform, including from a merchant selling products or services through our booking or shopping features, we share the information related to the transaction with that third party and their service providers and transaction fulfillment providers. By making the purchase, you are directing us to share your information in this way. These entities may use the information shared in accordance with their privacy policies.
						</Typography>

						{/* Your Rights */}
						<br />
						<Typography tag="h3" weight="bold">
							Your Rights
						</Typography>
						<Typography tag="p" weight="light">
							You may submit a request to know, access, correct or delete the information we have collected about you at
							{' '}
							<LocaleLink to="mailto:hello@lets-talk-families.com?Subject=Privacy Request">hello@lets-talk-families.com</LocaleLink>
							. You may appeal any decision we have made about your request by following the instructions in the communication you receive from us notifying you of our decision. You may also exercise your rights to know, access, correct, delete, or appeal by sending your request to the physical address provided in the “Contact Us” section below. You can also update your account information directly through your in-app settings, as well as request a copy of your Let&apos;s Talk data or request to deactivate or delete your account. Your right to know includes what personal information we have collected about you, including the categories of sources from which the personal information is collected, the business or commercial purpose for collecting or sharing personal information, the categories of third parties to whom we share the personal information, and the specific pieces of personal information we have collected about you. We do not discriminate based on the exercise of any privacy rights that you might have.
						</Typography>
						<Typography tag="p" weight="light">
							You may be entitled, in accordance with applicable law, to submit a request through an authorized agent. To designate an authorized agent to exercise choices on your behalf, please provide evidence that you have given such agent power of attorney or that the agent otherwise has valid written authority to submit requests to exercise rights on your behalf. We will respond to your request consistent with applicable law and subject to proper verification. We will verify your request by asking you to send it from the email address associated with your account or to provide information necessary to verify your account.
						</Typography>
						<Typography tag="p" weight="light">
							While some of the information that we collect and use may constitute sensitive personal information, we only process such information in order to provide the Platform and within other exemptions under applicable law. For example, we may process your financial information in order to provide you the goods or services you request from us or your driver’s license number in order to verify your identity.
						</Typography>

						{/* Your Choices */}
						<br />
						<Typography tag="h3" weight="bold">
							Your Choices
						</Typography>
						<Typography weight="light">
							<ul>
								<li>
									You may be able to control some of the Information We Collect through your device browser settings to refuse or disable
									{' '}
									<LocaleLink to="/legal/cookie-policy" target="_blank">Cookies</LocaleLink>
									. Because each browser is different, please consult the instructions provided by your browser. Please note that you may need to take additional steps to refuse or disable certain types of Cookies. In addition, your choice to disable Cookies is specific to the particular browser or device that you are using when you disable Cookies, so you may need to separately disable Cookies for each type of browser or device. If you choose to refuse, disable, or delete Cookies, some of the functionality of the Platform may no longer be available to you. Without this information, we are not able to provide you with all of the requested services.
								</li>
								<li>You can navigate to &quot;Ads&quot; in your in-app settings to opt-out of targeted advertising based on personal information about your activity on nonaffiliated apps and websites.</li>
								<li>
									You may be able to manage third-party advertising preferences for some of the third parties we work with to serve advertising across the Internet by using the choices available at
									{' '}
									<LocaleLink to="https://www.networkadvertising.org/managing/opt_out.asp" target="_blank">https://www.networkadvertising.org/managing/opt_out.asp</LocaleLink>
									{' '}
									and
									{' '}
									<LocaleLink to="https://www.aboutads.info/choices" target="_blank">https://www.aboutads.info/choices</LocaleLink>
									.
								</li>
								<li>Your device may have controls that determine what Information We Collect. For example, you can control whether we can collect your mobile advertising identifier for advertising through settings on your Apple and Android devices.</li>
								<li>You can opt out of marketing or advertising emails by using the “unsubscribe” link or mechanism noted in marketing or advertising emails.</li>
								<li>If you previously chose to share precise location information, you can prevent your device from sharing precise location information (e.g., GPS location information) with the Platform at any time through your device’s operating system settings.</li>
								<li>If you have registered for an account, you may access, review, and update certain personal information that you have provided to us by logging into your account and using available features and functionalities.</li>
								<li>Some browsers transmit “do-not-track” signals to websites. Because of differences in how browsers incorporate and activate this feature, we currently do not take action in response to these signals.</li>
							</ul>
						</Typography>

						{/* Data Security and Retention */}
						<br />
						<Typography tag="h3" weight="bold">
							Data Security and Retention
						</Typography>
						<Typography tag="p" weight="light">
							We use reasonable measures to help protect information from loss, theft, misuse, unauthorized access, disclosure, alteration, or destruction. You should understand that no data storage system or transmission of data over the Internet or any other public network can be guaranteed to be 100 percent secure. Please note that information collected by third parties may not have the same security protections as information you submit to us, and we are not responsible for protecting the security of such information.
						</Typography>
						<Typography tag="p" weight="light">
							We retain information for as long as necessary to provide the Platform and for the other purposes set out in this Privacy Policy. We also retain information when necessary to comply with contractual and legal obligations, when we have a legitimate business interest to do so (such as improving and developing the Platform, and enhancing its safety, security and stability), and for the exercise or defense of legal claims.
						</Typography>
						<Typography tag="p" weight="light">
							The retention periods are different depending on different criteria, such as the type of information and the purposes for which we use the information. For example, when we process your information such as your profile information to provide you with the Platform, we keep this information for as long as you have an account. If you violate our
							{' '}
							<LocaleLink to="/legal/terms" target="_blank">
								Terms of Service
							</LocaleLink>
							{' '}
							or other conditions or policies, we may remove your profile immediately, but may keep other information about you to process the violation.
						</Typography>
						<Typography id="children-privacy" tag="p" weight="light">
							Let&apos;s Talk may transmit your data to its servers or data centers outside of the United States for storage and/or processing. Other entities with whom Let&apos;s Talk may share your data as described herein may be located outside of the United States.
						</Typography>

						{/* Children’s Privacy */}
						<br />
						<Typography tag="h3" weight="bold">
							Children&apos;s Privacy
						</Typography>
						<Typography tag="p" weight="light">
							The privacy of children is important to us. We recognize the need to provide further privacy protections when it comes to children. We are committed to protecting the privacy of children who use our sites and applications.
						</Typography>
						<Typography tag="p" weight="light">
							Children under the age of 13 are only allowed to use Let&apos;s Talk with a parent or guardian. Children under the age of 13 are not permitted to create an account by themselves but instead must be invited to Let&apos;s Talk by a parent or guardian. Parents or guardians must initiate the registration process on behalf of children under the age of 13. Before collecting personal information from a child under the age of 13, we provide parents or guardians with our Notice to Parents regarding the collection, use, and disclosure of children&apos;s personal information and obtain verifiable parental consent. We use a consent mechanism during the registration process, such as an online payment transaction or other verifiable consent method permitted by applicable law, to confirm registrants are the parent or legal guardian of any child under the age of 13 for whom they register an account and to obtain consent to our collection and use of the child&apos;s personal information in accordance with this Privacy Policy. In those cases, we delete the personal information from children under 13 until a parent or guardian initiates a new registration.
						</Typography>
						<Typography tag="h4" weight="bold">
							Information We Collect from Children
						</Typography>

						<Typography tag="p" weight="light">
							We may collect the following personal information from children:
						</Typography>

						<ul>
							<li>
								<Typography tag="p" weight="light">
									Account and profile information, including a name or nickname, age, gender, username, and password
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									User-generated content, including responses to quiz-style questions and open-ended reflection activities, questions and comments, audio messages to parents, and activity ratings
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									Persistent identifiers, including information held in cookies, an Internet Protocol (IP) address, a processor or device serial number, or a unique device identifier
								</Typography>
							</li>
						</ul>

						<Typography tag="p" weight="light">
							When we collect personal information from a child, we will retain that information only so long as reasonably necessary to fulfill the activity requested or allow participation in the activity, to ensure the security of our users and our Platform, or as required by law.
						</Typography>

						<br />

						<Typography tag="h4" weight="bold">
							How We Use Information Collected from Children
						</Typography>

						<Typography tag="p" weight="light">
							We use the personal information we collect for the following purposes:
						</Typography>

						<ul>
							<li>
								<Typography tag="p" weight="light">
									To provide, maintain, and improve the Platform, including to enable participation in interactive features of the Platform.
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									To create, maintain, and service your account.
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									To communicate with you about your account, including to respond to customer service requests from you and to send notifications, newsletters, postcards, or messages you request.
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									To conduct internal research and development.
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									To analyze traffic and user activities on the Platform, identify popular areas or features, and optimize and personalize the Platform.
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									To debug the Platform to identify and repair errors.
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									To process or fulfill orders and transactions.
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									To protect the rights and property of Let&apos;s Talk and others and comply with our legal obligations, including to detect, investigate, and prevent fraud and other illegal activities, protect the security and integrity of the Platform, and to enforce our agreements.
								</Typography>
							</li>
						</ul>

						<br />

						<Typography tag="h4" weight="bold">
							How We Disclose Personal Information Collected from Children
						</Typography>

						<Typography tag="p" weight="light">
							Let&apos;s Talk may disclose personal information we collect for the following purposes:
						</Typography>

						<ul>
							<li>
								<Typography tag="p" weight="light">
									To operate the Platform and administer our business, such as to our service providers who perform a business, professional, or support function for us.
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									In connection with a corporate transaction, such as a merger, acquisition, or sale of our assets.
								</Typography>
							</li>

							<li>
								<Typography tag="p" weight="light">
									To protect the rights and property of Let&apos;s Talk and others, to comply with our legal obligations, including to respond to legal process or law enforcement requests or comply with mandatory reporting requirements; to detect, investigate and prevent fraud and other illegal activities; to protect the security and integrity of the Platform; and to enforce our agreements.
								</Typography>
							</li>
						</ul>

						<br />

						<Typography tag="h4" weight="bold">
							Parental Rights
						</Typography>

						<Typography tag="p" weight="light">
							Let&apos;s Talk will not collect, use, or disclose personal information from children without parental consent except in limited circumstances permitted by applicable law. For example, a child may provide us with a name and online contact information so that we can provide parental notice and request a parent&apos;s consent. If we learn that we have inadvertently collected the information of a child without parental consent, we will delete this information.
						</Typography>

						<Typography tag="p" weight="light">
							Parents can refuse to permit us to collect Personal Information from their child, request to review personal information we have collected, and request that we delete from our records the personal information we have collected regarding children. Please keep in mind that a request to delete records may lead to a termination of an account, membership, or other service.
						</Typography>

						<ul>
							<li>
								<Typography tag="p" weight="light">
									Parents can refuse to permit us to collect or use their child&apos;s information or submit a request to review or delete the personal information that we have collected from their child at any time by sending an email to us at
									{' '}
									<LocaleLink to="mailto:hello@lets-talk-families.com?Subject=Parental Privacy Request">
										hello@lets-talk-families.com
									</LocaleLink>
									. For privacy and security, we may require you to take certain steps or provide additional information to verify your identity before we provide any personal information.
								</Typography>
							</li>
						</ul>

						<Typography tag="p" weight="light">
							If you are a parent or guardian, please contact us for information and resources to help you understand the Platform and the tools and controls you can use.
						</Typography>

						<Typography tag="p" weight="light">
							All inquiries may be directed to:
						</Typography>

						<Typography tag="p" weight="light">
							Teen Health Research, Inc. d/b/a Let&apos;s Talk Families
							<br />
							1809 Kenwyck Manor Way
							<br />
							Raleigh, NC 27612
							<br />
							Email:
							{' '}
							<LocaleLink to="mailto:hello@lets-talk-families.com">
								hello@lets-talk-families.com
							</LocaleLink>
							<br />
							Phone: 919-798-7003
						</Typography>

						{/* Other Rights */}
						<br />
						<Typography tag="h3" weight="bold">
							Other Rights
						</Typography>

						{/* Sharing for Direct Marketing Purposes (Shine the Light) */}
						<Typography tag="h4" weight="bold">
							Sharing for Direct Marketing Purposes (Shine the Light)
						</Typography>
						<Typography tag="p" weight="light">
							If you are a California resident, once a calendar year, you may be entitled to obtain information about personal information that we shared, if any, with other businesses for their own direct marketing uses. To submit a request, contact us at:
							{' '}
							<LocaleLink to="mailto:hello@lets-talk-families.com?Subject=Privacy Request">hello@lets-talk-families.com</LocaleLink>
							.
						</Typography>

						{/* Content Removal for Users Under 18 */}
						<Typography tag="h4" weight="bold">
							Content Removal for Users Under 18
						</Typography>
						<Typography tag="p" weight="light">
							Users of the Platform who are California residents and are under 18 years of age may request and obtain removal of User Content they posted by contacting us at:
							{' '}
							<LocaleLink to="mailto:hello@lets-talk-families.com?Subject=California Removal Request">hello@lets-talk-families.com</LocaleLink>
							. All requests must provide a description of the User Content you want removed and information reasonably sufficient to permit us to locate that User Content. We do not accept California Removal Requests via postal mail, telephone, or facsimile. We may not be able to respond if you do not provide adequate information. Please note that your request does not ensure complete or comprehensive removal of the material. For example, User Content that you have posted may be republished or reposted by another user.
						</Typography>

						{/* Privacy Policy Updates */}
						<br />
						<Typography tag="h3" weight="bold">
							Privacy Policy Updates
						</Typography>
						<Typography tag="p" weight="light">
							We may update this Privacy Policy from time to time. When we update the Privacy Policy, we will notify you by updating the “Last Updated” date at the top of the new Privacy Policy, posting the new Privacy Policy, or providing any other notice required by applicable law. We recommend that you review the Privacy Policy each time you visit the Platform to stay informed of our privacy practices.
						</Typography>

						{/* Contact Us */}
						<br />
						<Typography tag="h3" weight="bold">
							Contact Us
						</Typography>
						<Typography tag="p" weight="light">
							If you have any questions about this policy or your rights when using our product, please email us at
							{' '}
							<LocaleLink to="mailto:hello@lets-talk-families.com?Subject=Privacy Policy">hello@lets-talk-families.com</LocaleLink>
							.
						</Typography>
					</S.Wrapper>
				</LegalNavigation>
			</ContentNavigation>
		</>
	);
};


/**
 * Configuration
 */

PrivacyPolicy.propTypes = {
	meta: PropTypes.shape(),
	locale: PropTypes.shape()
};
PrivacyPolicy.defaultProps = {
	meta: {},
	locale: {}
};


/**
 * Exports
 */

export default PrivacyPolicy;
